import { makeAutoObservable, reaction } from 'mobx';
import axios from 'axios';
import i18next from 'i18next';

import {
  getLeaderboardByIdApi,
  getLeaderboardListApi,
  setUserByIdApi,
} from '@/lib/api/leaderboards';
import { logger } from '@/lib/api/logger';
import { dateHelper } from '@/helpers/dateHelper';
import { formatCurrency } from '@/helpers/formatCurrency';
import { currencySigns } from '@/config/constants';

const { usd = '$' } = currencySigns || {};

export class Leaderboards {
  constructor(model) {
    makeAutoObservable(this);

    this.root = model;

    reaction(
      () => [this.root.auth.brand, this.root.profile?.currency?.name],
      (val) => {
        const [brand, currency] = val;

        if (!(brand && currency)) {
          return;
        }

        // prevent data loading for /leaderboards page with entire list
        // but keep data loading for /leaderboards/:id
        if (window.location.href.includes('/all-users-tournaments')) {
          return;
        }

        this.getLeaderboardList({ brand, currency });
      },
    );
  }

  leaderboardList = [];

  // Cache for leaderboards by id
  // key - tournamentId, value - expiresAt timestamp
  leaderboardsExpiresList = new Map();

  leaderboardsByIds = new Map();

  leaderboardRulesHtml = null;

  userJoinIsProgress = false;

  getLeaderboardList = async ({ brand, currency }) => {
    try {
      const {
        data: { data = {} },
      } = await getLeaderboardListApi({
        currency,
        partnerId: brand,
      });

      const response = data || { data: [] };

      if (response.data.length) {
        const now = Date.now(); // Cache current time once

        this.leaderboardList = response.data.sort((a, b) => {
          const aEnded = now >= new Date(a.endsAt).getTime();
          const bEnded = now >= new Date(b.endsAt).getTime();

          if (aEnded && !bEnded) return 1; // Finished tournaments go last
          if (!aEnded && bEnded) return -1; // Active tournaments go first

          // Both finished - sort by end date (newest first)
          if (aEnded && bEnded) {
            return new Date(b.endsAt).getTime() - new Date(a.endsAt).getTime();
          }

          // Both active - sort by participation then start date
          if (a.participated && !b.participated) return -1;
          if (!a.participated && b.participated) return 1;

          // Same participation status - sort by start date
          return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        logger.error('getLeaderboardList failed', {
          error,
        });
      }
    }
  };

  fetchLeaderboardById = async (tournamentId = '') => {
    const leaderboardExpiresAt = this.leaderboardsExpiresList.get(tournamentId);

    if ((leaderboardExpiresAt && leaderboardExpiresAt > Date.now()) || !tournamentId) {
      return;
    }

    const expiresAt = Date.now() + 0.5 * 60 * 1000; // Cache for 30 seconds
    this.leaderboardsExpiresList.set(tournamentId, expiresAt);

    const {
      profile: {
        currency: { name: currency = '' },
      },
    } = this.root;
    const opts = { currency, tournamentId };

    try {
      const {
        data: { data = {} },
      } = await getLeaderboardByIdApi(opts);

      this.leaderboardsByIds.set(tournamentId, data);
    } catch (error) {
      if (error instanceof Error) {
        logger.error('fetchLeaderboardById failed', {
          error,
        });
      }
    }
  };

  userJoinTournament = async (tournamentId = '') => {
    try {
      this.leaderboardsExpiresList.delete(tournamentId);
      const {
        data: { data = {} },
      } = await setUserByIdApi(tournamentId);

      const { userRating = {} } = data;

      return userRating;
    } catch (error) {
      if (error instanceof Error) {
        logger.error('userJoinTournament failed', {
          error,
        });
      }
    } finally {
      this.setUserJoinIsProgress(false);
    }
  };

  getFile = async (url = '') => {
    let res = { data: '' };
    try {
      res = await axios.get(url);
    } catch (error) {
      if (error instanceof Error) {
        logger.error(`Get ${url} failed`, {
          error,
        });
      }
    }
    return res;
  };

  hasFile = (res = {}) => {
    return !res.data.includes('<div id="sportsbook-app" class="app">');
  };

  getPlacePrizes = (prizes = []) => {
    let html = '';
    prizes.forEach(({ place, prize }) => {
      switch (true) {
        case `${place.trim()}` === '1':
          html += `<p>${i18next.t('STATS.COMMON.1PLACE', {
            defaultValue: '1st place',
          })} - ${prize}</p>`;
          break;
        case `${place.trim()}` === '2':
          html += `<p>${i18next.t('STATS.COMMON.2PLACE', {
            defaultValue: '2nd place',
          })} - ${prize}</p>`;
          break;
        case `${place.trim()}` === '3':
          html += `<p>${i18next.t('STATS.COMMON.3PLACE', {
            defaultValue: '3rd place',
          })} - ${prize}</p>`;
          break;
        default:
          html += `<p class="_default-place">${place.trim()} ${i18next.t('COMMON.LD_PLACE', {
            defaultValue: 'place',
          })} - ${prize}</p>`;
          break;
      }
    });

    return html;
  };

  getAllPrizePlaces = (prizes = []) => {
    const { place } = prizes.slice(-1)[0] || { place: '' };
    return place.includes('-') ? place.split('-').slice(-1)[0].trim() : place.trim();
  };

  getDate = (date = '') => {
    const { fullTimeDate } = dateHelper(new Date(date).getTime() / 1000);
    return fullTimeDate;
  };

  convertTypesToString = (types = []) => {
    const aliases = {
      1: 'COMMON.PREMATCH_CAPITALIZE',
      2: 'COMMON.LIVE_CAPITALIZE',
      express: 'RX.BETSLIP.TAB_EXPRESS',
      ordinary: 'RX.BETSLIP.TAB_SINGLEBET',
    };

    return (
      types
        .map((type) => i18next.t(aliases[type], { defaultValue: '' }))
        .filter(Boolean)
        .join(', ') || i18next.t('COMMON.LEADERBOARD_ANY', { defaultValue: 'any' })
    );
  };

  getRuleMap = async (rating = {}) => {
    const {
      title = '',
      startsAt = '',
      endsAt = '',
      minAmount = null,
      minCoefficient = 0,
      minExpressCoefficient = 0,
      prizePool = '',
      prizePoolCurrency = usd,
      prizes = [],
      disciplineIds = [],
      availableTournaments = [],
      ticketType = [],
      stages = [],
      maxbet = null,
    } = rating;

    const {
      profile: {
        currency: { sign },
        isGuest = false,
      },
      ui: { viewType },
    } = this.root;

    const getDiffDays = () => {
      return Math.floor(
        (new Date(endsAt).getTime() - new Date(startsAt).getTime()) / (1000 * 60 * 60 * 24),
      );
    };
    const getStringListFromArr = (arr) => {
      return arr.length
        ? arr.join(', ')
        : i18next.t('COMMON.LEADERBOARD_ANY', { defaultValue: 'any' });
    };

    const disciplinesList = getStringListFromArr(disciplineIds);
    const tournamentsList = getStringListFromArr(availableTournaments.map(({ name }) => name));
    const placePrizes = prizes.slice().sort((a, b) => a.index - b.index);

    const rulesMap = new Map();
    rulesMap.set('name', `"${title}"`);
    rulesMap.set('startDate', this.getDate(startsAt));
    rulesMap.set('finishDate', this.getDate(endsAt));
    rulesMap.set('days', getDiffDays());

    const isSocialView = viewType === 'social';

    // User currency defined by current user currency
    const userCurrency = (isGuest && !isSocialView) || !sign ? usd : sign;

    const minBetAmount = minAmount
      ? formatCurrency({ currency: userCurrency, num: minAmount })
      : i18next.t('COMMON.LEADERBOARD_ANY', { defaultValue: 'any' });

    rulesMap.set('minBet', minBetAmount);

    rulesMap.set(
      'minOdds',
      minCoefficient || i18next.t('COMMON.LEADERBOARD_ANY', { defaultValue: 'any' }),
    );
    rulesMap.set(
      'minExpressCoefficient',
      minExpressCoefficient || i18next.t('COMMON.LEADERBOARD_ANY', { defaultValue: 'any' }),
    );

    // Use prize pool currency for prize pool sum
    rulesMap.set('prizePoolSum', `${prizePoolCurrency}${prizePool}`);
    rulesMap.set('numberWinners', this.getAllPrizePlaces(placePrizes));
    rulesMap.set('prizes', this.getPlacePrizes(placePrizes));
    rulesMap.set('disciplineList', `${disciplinesList}.`);
    rulesMap.set('tournamentsList', `${tournamentsList}.`);
    rulesMap.set('By_bet_amount', '');
    rulesMap.set('By_win_amount_(payout)', '');
    rulesMap.set(
      'betType',
      `<span class="_ut-types">${this.convertTypesToString(ticketType)}</span>`,
    );
    rulesMap.set('lineType', `<span class="_ut-types">${this.convertTypesToString(stages)}</span>`);

    const maxBetAmount = maxbet
      ? formatCurrency({ currency: userCurrency, num: maxbet })
      : i18next.t('COMMON.LEADERBOARD_ANY', { defaultValue: 'any' });
    rulesMap.set('maxBet', maxBetAmount);

    return rulesMap;
  };

  convertRulesToHtml = async (initialHtml = '', ruleMap = new Map()) => {
    let html = initialHtml;

    for (const [key, val] of ruleMap) {
      html = html.replace(`{{${key}}}`, val);
    }

    return html;
  };

  getLeaderboardRulesHtml = async (leaderboardInfo = {}) => {
    const {
      ui: { language = 'en' },
      store: { assets },
    } = this.root;

    const basePath = 'dist/i18n';
    const path = 'static';
    const defaultLang = 'en';
    const targetFile = 'UsersTournament';

    const pathOfTheLang = `${basePath}/${path}/${language}`;
    const pathOfTheDefaultLang = `${basePath}/${path}/${defaultLang}`;

    const filePath = assets[`${pathOfTheLang}/${targetFile}.html`];
    const filePathByDefaultLang = assets[`${pathOfTheDefaultLang}/${targetFile}.html`];

    let res = { data: '' };

    try {
      res = await axios.get(filePath);

      if (!(this.hasFile(res) && res.data)) {
        res = await this.getFile(filePathByDefaultLang);
      }
    } catch (error) {
      if (error instanceof Error) {
        logger.error('getLeaderboardRulesHtml failed', {
          error,
        });
      }
      res = await this.getFile(filePathByDefaultLang);
    }

    const { data = '' } = res || {};

    const ruleMap = await this.getRuleMap(leaderboardInfo);
    const resultHtml = await this.convertRulesToHtml(data, ruleMap);

    this.leaderboardRulesHtml = resultHtml;
  };

  setUserJoinIsProgress = (bool = false) => {
    this.userJoinIsProgress = bool;
  };
}
